import dayjs from "dayjs"
import { formatPlanPriceAmount } from "./plansPricingGrid"

/**
 * @param {unknown} payload - axios response.data
 * @returns {object[]}
 */
export function parseAdminPlansListResponse(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload.plans)) return payload.plans
  if (payload.success && Array.isArray(payload.result)) return payload.result
  if (payload.success && Array.isArray(payload.results)) return payload.results
  return []
}

/**
 * @param {unknown} payload - axios response.data
 */
export function parseAdminPlansListMeta(payload) {
  if (!payload || Array.isArray(payload)) {
    return { count: Array.isArray(payload) ? payload.length : 0, next: null, previous: null }
  }
  return {
    count: payload.count ?? payload.total ?? 0,
    next: payload.next ?? null,
    previous: payload.previous ?? null,
  }
}

function capitalizeStatus(value) {
  const v = String(value || "")
    .trim()
    .toLowerCase()
  if (v === "active") return "Active"
  if (v === "inactive") return "Inactive"
  if (v === "draft") return "Draft"
  if (!value) return "Draft"
  return String(value).charAt(0).toUpperCase() + String(value).slice(1)
}

function normalizePlanType(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
  if (v === "one_time" || v === "onetime" || v === "one-time") return "One-Time"
  if (v === "subscription" || v === "sub") return "Subscription"
  if (raw === "One-Time" || raw === "Subscription") return raw
  return raw || "—"
}

function normalizePlanCategory(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (v === "organization" || v === "organisation" || v === "org") {
    return "Organization"
  }
  if (v === "standard") return "Standard"
  if (raw === "Standard" || raw === "Organization") return raw
  return "Standard"
}

function buildPriceLabel(plan, planType, lang = "en") {
  if (plan.price_label || plan.priceLabel) {
    return plan.price_label || plan.priceLabel
  }

  const amount = plan.price ?? plan.plan_price ?? plan.amount ?? 0
  const formatted = formatPlanPriceAmount(amount)
  const cycle = String(
    plan.billing_cycle || plan.billingCycle || plan.duration_type || ""
  )
    .trim()
    .toLowerCase()

  if (planType === "Subscription") {
    const suffix =
      lang === "ar"
        ? cycle.includes("year")
          ? "/سنة"
          : "/شهر"
        : cycle.includes("year")
          ? "/year"
          : "/month"
    return `SAR ${formatted}${suffix}`
  }

  return `SAR ${formatted}`
}

function formatLastUpdated(plan) {
  const raw =
    plan.last_updated ||
    plan.lastUpdated ||
    plan.updated_at ||
    plan.updatedAt ||
    plan.modified_at ||
    plan.time_updated

  if (!raw) return "—"
  const parsed = dayjs(raw)
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(raw)
}

function getAdminPlanCategory(plan) {
  const row = plan?.plan ?? plan
  const raw =
    row?.plan_type ??
    row?.planType ??
    row?.plan_category ??
    row?.planCategory ??
    ""
  return String(raw).trim().toLowerCase().replace(/[\s-]+/g, "_")
}

function isOrganizationAdminPlan(plan) {
  const row = plan?.plan ?? plan
  if (!row || typeof row !== "object") return false

  if (
    row.is_organization === true ||
    row.is_organization_plan === true ||
    row.is_org_plan === true ||
    row.for_organization === true
  ) {
    return true
  }

  const category = getAdminPlanCategory(plan)
  if (
    category === "organization" ||
    category === "organisation" ||
    category === "org" ||
    category.includes("organization") ||
    category.includes("organisation")
  ) {
    return true
  }

  const name = String(row.name ?? row.name_en ?? row.name_english ?? "")
    .trim()
    .toLowerCase()
  return /\bteam\s+plan\b/i.test(name)
}

/** Excludes organization pool plans from AI Users → Create dashboard user. */
export function filterPlansForDashboardUserCreate(plans) {
  if (!Array.isArray(plans)) return []
  return plans.filter((plan) => !isOrganizationAdminPlan(plan))
}

export function pickLocalizedField(plan, lang, enKey, arKey, fallbackKey) {
  if (lang === "ar") {
    return (
      plan[arKey] ??
      plan.name_arabic ??
      plan[fallbackKey] ??
      plan[enKey] ??
      plan.name ??
      plan.title ??
      ""
    )
  }
  return (
    plan[enKey] ??
    plan.name ??
    plan[fallbackKey] ??
    plan[arKey] ??
    plan.name_arabic ??
    plan.title ??
    ""
  )
}

export function mapPlansToSelectOptions(plans, lang) {
  if (!Array.isArray(plans)) return []
  return plans.map((plan) => ({
    value: Number(plan.id ?? plan.pk),
    label:
      pickLocalizedField(plan, lang, "name_en", "name_ar", "name") ||
      String(plan.id ?? ""),
  }))
}

/**
 * Maps GET /account/admin/plans/ item to Plans & Pricing table row.
 * @param {object} plan
 * @param {string} [lang]
 */
export function mapAdminPlanToTableRow(plan, lang = "en") {
  const type = normalizePlanType(plan.type)
  const category = normalizePlanCategory(
    plan.plan_type ?? plan.plan_category ?? plan.planCategory
  )
  const status = capitalizeStatus(plan.status)

  const discountRaw =
    plan.discount_percent ??
    plan.discount_percentage ??
    plan.discountPercent ??
    plan.discount
  const discountPercent =
    discountRaw == null || discountRaw === "" ? null : Number(discountRaw)

  const duration = String(
    plan.billing_cycle || plan.billingCycle || plan.duration_type || plan.sub_name || ""
  )
    .trim()
    .toLowerCase()
  const billingCycle = duration.includes("year") ? "Yearly" : "Monthly"

  const discountedPriceRaw =
    plan.discounted_price ?? plan.discountedPrice ?? plan.yearly_price
  const discountedPrice =
    discountedPriceRaw != null && discountedPriceRaw !== ""
      ? Number(discountedPriceRaw)
      : 0

  const addonRaw =
    plan.addon_per_photo ??
    plan.addon_price_per_photo ??
    plan.per_photo_price ??
    plan.photo_addon_price

  return {
    id: String(plan.id ?? plan.pk ?? plan.uuid ?? ""),
    name: pickLocalizedField(plan, lang, "name_en", "name_ar", "name"),
    description:
      pickLocalizedField(
        plan,
        lang,
        "description",
        "description_arabic",
        "short_description"
      ) ||
      pickLocalizedField(
        plan,
        lang,
        "short_description_en",
        "short_description_ar",
        "short_description"
      ) ||
      "",
    type,
    category,
    price: Number(plan.price ?? plan.plan_price ?? 0) || 0,
    priceLabel: buildPriceLabel(plan, type, lang),
    discountPercent: Number.isNaN(discountPercent) ? null : discountPercent,
    discounted: Boolean(plan.discounted),
    discountedPrice: Number.isNaN(discountedPrice) ? 0 : discountedPrice,
    billingCycle,
    isPopular: Boolean(
      plan.most_popular ??
        plan.is_most_popular ??
        plan.highlighted ??
        plan.is_popular
    ),
    addonPerPhoto:
      addonRaw != null && addonRaw !== "" && !Number.isNaN(Number(addonRaw))
        ? Number(addonRaw)
        : null,
    images:
      plan.images ??
      plan.images_count ??
      plan.number_of_images ??
      plan.image_count ??
      0,
    subscribers:
      plan.subscriber_count ??
      plan.subscribers ??
      plan.subscribers_count ??
      plan.active_subscribers ??
      plan.active_subscribers_count ??
      0,
    status,
    lastUpdated: plan.last_updated || formatLastUpdated(plan),
    _raw: plan,
  }
}
